import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
    ArrowDownAZ,
    ArrowUpAZ,
    CalendarArrowDown,
    CalendarArrowUp,
    LayoutGrid,
    List,
    Plus,
    Search,
    Settings,
} from "lucide-react";
import { AudiobookBadge } from "../../../../ui/components/AudiobookBadge/AudiobookBadge";
import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Grid,
  HStack,
  Heading,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { type Book, type PriceTier } from "../../types/database";
import { useAuth } from "../../services/auth/AuthProvider";
import {
  deleteBook,
  getBooksForPublisher,
} from "../../services/api/books";
import { getPriceValues } from "../../services/api/prices";
import { updateAdmin } from "../../services/api/admin";
import Seo from "../../../../ui/components/Seo/Seo";

import { useBooksPageState, type BookSort } from "./useBooksPageState";

function priceLabel(priceId: string, tiers: PriceTier[]): string {
  const tier = tiers.find((x) => x.tierName === priceId);
  return tier ? `$${tier.priceUSD.toFixed(2)}` : "";
}

const SORT_OPTIONS: { value: BookSort; label: string; icon: typeof ArrowDownAZ }[] = [
  { value: "name", label: "За алфавітом", icon: ArrowDownAZ },
  { value: "nameReversed", label: "За зворотнім алфавітом", icon: ArrowUpAZ },
  { value: "createdAt", label: "Спочатку новіші", icon: CalendarArrowDown },
  { value: "createdAtReversed", label: "Спочатку старіші", icon: CalendarArrowUp },
];

export function BooksPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { firebaseUser, admin, publishingHouse, isSuperadmin } = useAuth();

  const publisherName = publishingHouse?.name ?? "";

  const booksQuery = useQuery(
      ["books", publisherName],
      () => getBooksForPublisher(publisherName),
      { enabled: Boolean(publisherName) },
  );
  const pricesQuery = useQuery(["prices"], getPriceValues);
  const priceTiers = pricesQuery.data ?? [];

  const [isGrid, setIsGrid] = useState(admin?.isGrid ?? false);
  const { query, setQuery, sort, setSort, filterPublic, setFilterPublic } =
      useBooksPageState(Boolean(booksQuery.data));
  const [deleteTarget, setDeleteTarget] = useState<Book | null>(null);

  const deleteMutation = useMutation((id: string) => deleteBook(id), {
    onSuccess: () => {
      toast({ description: "Книгу успішно видалено!", status: "success" });
      queryClient.invalidateQueries(["books", publisherName]);
    },
    onError: () => {
      toast({ description: "Сталась помилка!", status: "error" });
    },
    onSettled: () => setDeleteTarget(null),
  });

  const books = useMemo(() => {
    let list = booksQuery.data ?? [];
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
          (b) =>
              b.name.toLowerCase().includes(q) ||
              b.authors.join(", ").toLowerCase().includes(q),
      );
    }
    if (filterPublic !== null) {
      list = list.filter((b) => b.isPublic === filterPublic);
    }
    const sorted = [...list];
    sorted.sort((a, b) => {
      switch (sort) {
        case "nameReversed":
          return b.name.localeCompare(a.name);
        case "createdAt":
          return (b.createdAt || "").localeCompare(a.createdAt || "");
        case "createdAtReversed":
          return (a.createdAt || "").localeCompare(b.createdAt || "");
        default:
          return a.name.localeCompare(b.name);
      }
    });
    return sorted;
  }, [booksQuery.data, query, sort, filterPublic]);

  function toggleGrid(next: boolean) {
    setIsGrid(next);
    if (firebaseUser) updateAdmin(firebaseUser.uid, { isGrid: next }).catch(() => {});
  }

  const filtersActive = sort !== "name" || filterPublic !== null;

  // Rendered twice: next to the title on narrow screens, at the end of the
  // toolbar once everything fits on a single row.
  const addButton = (
      <Button
          flexShrink={0}
          leftIcon={<Icon as={Plus} boxSize={4} />}
          onClick={() => navigate("/publishers/panel/books/new")}
      >
        {"Додати"}
      </Button>
  );

  return (
      <Flex direction="column" minH="100%">
        <Seo
          title="Книги — Кабінет видавця"
          description="Керування книгами видавництва"
          noindex
        />
        <Flex
            as="header"
            position="sticky"
            top={0}
            zIndex={20}
            direction={{ base: "column", lg: "row" }}
            align={{ base: "stretch", lg: "center" }}
            justify="space-between"
            gap={3}
            bg="white"
            borderBottom="1px solid"
            borderColor="blackAlpha.100"
            px={{ base: 4, md: 6 }}
            py={{ base: 3, md: 4 }}
        >
          <Flex align="center" justify="space-between" gap={3} minW={0}>
            <Heading
                size={{ base: "md", md: "lg" }}
                fontWeight="medium"
                letterSpacing="wide"
                textTransform="uppercase"
                noOfLines={1}
            >
              {"Книги"}
            </Heading>
            <Box display={{ base: "block", lg: "none" }}>{addButton}</Box>
          </Flex>

          {/* `gap` (not HStack's spacing) so wrapped rows keep vertical spacing too. */}
          <Flex gap={2} flexWrap="wrap" align="center" justify="flex-end" minW={0}>
            <InputGroup flex={{ base: "1 1 10rem", lg: "0 0 auto" }} w={{ lg: "56" }} minW={0} size="sm">
              <InputLeftElement pointerEvents="none">
                <Icon as={Search} boxSize={4} color="gray.400" />
              </InputLeftElement>
              <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={"Пошук"}
                  borderRadius="full"
              />
            </InputGroup>

            <Popover placement="bottom-end">
              <PopoverTrigger>
                <IconButton
                    aria-label="settings"
                    size="sm"
                    variant={filtersActive ? "solid" : "ghost"}
                    icon={<Icon as={Settings} boxSize={4} />}
                />
              </PopoverTrigger>
              <PopoverContent w="64">
                <PopoverArrow />
                <PopoverBody>
                  <SortFilterControls
                      sort={sort}
                      filterPublic={filterPublic}
                      onApply={(s, f) => {
                        setSort(s);
                        setFilterPublic(f);
                      }}
                  />
                </PopoverBody>
              </PopoverContent>
            </Popover>

            <HStack spacing={0.5} flexShrink={0} border="1px solid" borderColor="blackAlpha.200" borderRadius="full" p={0.5}>
              <IconButton
                  aria-label="list"
                  size="sm"
                  variant={isGrid ? "ghost" : "solid"}
                  borderRadius="full"
                  icon={<Icon as={List} boxSize={4} />}
                  onClick={() => toggleGrid(false)}
              />
              <IconButton
                  aria-label="grid"
                  size="sm"
                  variant={isGrid ? "solid" : "ghost"}
                  borderRadius="full"
                  icon={<Icon as={LayoutGrid} boxSize={4} />}
                  onClick={() => toggleGrid(true)}
              />
            </HStack>

            <Box display={{ base: "none", lg: "block" }}>{addButton}</Box>
          </Flex>
        </Flex>

        <Box flex={1} p={{ base: 4, md: 6 }}>
          {booksQuery.isLoading ? (
              <Center h="64">
                <Spinner color="brand.orange" />
              </Center>
          ) : books.length === 0 ? (
              <Center h="64">
                <Text color="gray.500">{"Немає книг!"}</Text>
              </Center>
          ) : (
              <>
                <Text mb={4} fontSize="sm" color="gray.500">
                  {books.length}
                </Text>
                {isGrid ? (
                    <Grid
                        templateColumns={{
                          base: "repeat(auto-fill, minmax(140px, 1fr))",
                          md: "repeat(auto-fill, minmax(180px, 1fr))",
                        }}
                        gap={{ base: 3, md: 4 }}
                    >
                      {books.map((book) => (
                          <BookCard
                              key={book.id}
                              book={book}
                              isGrid
                              showId={isSuperadmin}
                              priceTiers={priceTiers}
                              onEdit={() => navigate(`/publishers/panel/books/${encodeURIComponent(book.id)}/edit`)}
                              onDelete={() => setDeleteTarget(book)}
                          />
                      ))}
                    </Grid>
                ) : (
                    <Flex direction="column" gap={3}>
                      {books.map((book) => (
                          <BookCard
                              key={book.id}
                              book={book}
                              isGrid={false}
                              showId={isSuperadmin}
                              priceTiers={priceTiers}
                              onEdit={() => navigate(`/publishers/panel/books/${encodeURIComponent(book.id)}/edit`)}
                              onDelete={() => setDeleteTarget(book)}
                          />
                      ))}
                    </Flex>
                )}
              </>
          )}
        </Box>

        <DeleteDialog
            target={deleteTarget}
            pending={deleteMutation.isLoading}
            onCancel={() => setDeleteTarget(null)}
            onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        />
      </Flex>
  );
}

function BookCard({
                    book,
                    isGrid,
                    showId,
                    priceTiers,
                    onEdit,
                    onDelete,
                  }: {
  book: Book;
  isGrid: boolean;
  showId: boolean;
  priceTiers: PriceTier[];
  onEdit: () => void;
  onDelete: () => void;
}) {
  const authors = book.authors.join(", ");
  const displayName = book.name.trim() || "(без назви)";
  const tintBorder = book.isPublic ? "green.200" : "red.200";
  const tintBg = book.isPublic ? "green.50" : "red.50";

  const actions = (
      <HStack spacing={1} flexShrink={0}>
        <Button variant="ghost" size="sm" px={{ base: 2, md: 3 }} onClick={onEdit}>
          {"Редагувати"}
        </Button>
        <Button variant="ghost" size="sm" px={{ base: 2, md: 3 }} color="red.500" onClick={onDelete}>
          {"Видалити"}
        </Button>
      </HStack>
  );

  if (isGrid) {
    return (
        <Flex direction="column" gap={2} borderRadius="lg" border="1px solid" borderColor={tintBorder} bg={tintBg} p={3}>
          <BookCover src={book.cover} alt={displayName} isGrid isAudiobook={book.type=="audiobook"} />
          <Box flex={1} minW={0} minH={0}>
            <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
              {displayName}
            </Text>
            <Text fontSize="xs" color="gray.500" noOfLines={1}>
              {authors}
            </Text>
          </Box>
          {actions}
        </Flex>
    );
  }

  return (
      <Flex align={{ base: "flex-start", md: "center" }} gap={{ base: 3, md: 4 }} borderRadius="lg" border="1px solid" borderColor={tintBorder} bg={tintBg} p={3}>
        <BookCover src={book.cover} alt={displayName} isGrid={false} isAudiobook={book.type=="audiobook"}/>
        {/* Title and meta sit side by side on wide screens and stack under each
            other on mobile, where a single row leaves no space for the title. */}
        <Flex
            direction={{ base: "column", md: "row" }}
            align={{ base: "stretch", md: "center" }}
            gap={{ base: 2, md: 4 }}
            flex={1}
            minW={0}
        >
          <Box flex={1} minW={0}>
            <Text fontSize="sm" fontWeight="medium" noOfLines={2}>
              {displayName}
            </Text>
            <Text fontSize="xs" color="gray.500" noOfLines={1}>
              {authors}
            </Text>
            {showId && (
                <Text fontFamily="mono" fontSize="10px" color="blackAlpha.500" noOfLines={1}>
                  {book.id}
                </Text>
            )}
          </Box>
          <Flex align="center" gap={2} flexWrap="wrap" flexShrink={0}>
            <Badge
                borderRadius="full"
                px={2}
                py={0.5}
                colorScheme={book.isPublic ? "green" : "red"}
                textTransform="none"
            >
              {book.isPublic ? "Опубліковано" : "Чернетка"}
            </Badge>
            <Text fontSize="xs" color="gray.500">
              {priceLabel(book.priceId, priceTiers)}
            </Text>
            {actions}
          </Flex>
        </Flex>
      </Flex>
  );
}

function SortFilterControls({
                              sort,
                              filterPublic,
                              onApply,
                            }: {
  sort: BookSort;
  filterPublic: boolean | null;
  onApply: (sort: BookSort, filterPublic: boolean | null) => void;
}) {
  const [tempSort, setTempSort] = useState<BookSort>(sort);
  const [tempFilter, setTempFilter] = useState<boolean | null>(filterPublic);

  return (
      <Box>
        <Text px={2} py={1.5} fontSize="xs" fontWeight="medium" color="gray.500" textTransform="uppercase">
          {"Сортування"}
        </Text>
        {SORT_OPTIONS.map((o) => (
            <HStack
                key={o.value}
                as="button"
                w="full"
                spacing={2}
                px={2}
                py={2}
                borderRadius="md"
                bg={tempSort === o.value ? "brand.orange" : "transparent"}
                color={tempSort === o.value ? "white" : "inherit"}
                _hover={{ bg: tempSort === o.value ? "brand.orange" : "blackAlpha.50" }}
                onClick={() => setTempSort(o.value)}
            >
              <Icon as={o.icon} boxSize={4} />
              <Text fontSize="sm">{o.label}</Text>
            </HStack>
        ))}

        <Text mt={2} px={2} py={1.5} fontSize="xs" fontWeight="medium" color="gray.500" textTransform="uppercase">
          {"Фільтрування"}
        </Text>
        {([
          [true, "Тільки публічні"],
          [false, "Тільки непублічні"],
        ] as const).map(([val, label]) => (
            <HStack
                key={label}
                as="button"
                w="full"
                spacing={2}
                px={2}
                py={2}
                borderRadius="md"
                _hover={{ bg: "blackAlpha.50" }}
                onClick={() => setTempFilter(tempFilter === val ? null : val)}
            >
              <Center boxSize={4} borderRadius="full" border="1px solid" borderColor={tempFilter === val ? "brand.orange" : "blackAlpha.400"}>
                {tempFilter === val && <Box boxSize={2} borderRadius="full" bg="brand.orange" />}
              </Center>
              <Text fontSize="sm">{label}</Text>
            </HStack>
        ))}

        <Flex mt={2} pt={2} borderTop="1px solid" borderColor="blackAlpha.100" justify="space-between">
          <Button variant="ghost" size="sm" onClick={() => { setTempSort("name"); setTempFilter(null); }}>
            {"Очистити"}
          </Button>
          <Button size="sm" onClick={() => onApply(tempSort, tempFilter)}>
            {"Застосувати"}
          </Button>
        </Flex>
      </Box>
  );
}

function BookCover({
                       src,
                       alt,
                       isGrid,
                       isAudiobook,
                   }: {
    src: string;
    alt: string;
    isGrid: boolean;
    isAudiobook?: boolean;
}) {
    const dims = isGrid
        ? { h: { base: "36", md: "44" }, w: "full" }
        : { h: "20", w: "14" };

    return (
        <Box position="relative" flexShrink={0}>
            <Image
                src={src || "/book-placeholder.png"}
                fallbackSrc="/book-placeholder.png"
                alt={alt}
                h={dims.h}
                w={dims.w}
                objectFit={src ? "cover" : "contain"}
                borderRadius="md"
                bg="blackAlpha.50"
            />

            {isAudiobook && <AudiobookBadge size={isGrid ? "md" : "sm"} />}
        </Box>
    );
}

function DeleteDialog({
                        target,
                        pending,
                        onCancel,
                        onConfirm,
                      }: {
  target: Book | null;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { isOpen, onClose } = useDisclosure({ isOpen: target !== null, onClose: onCancel });

  return (
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {"Видалити"} {target?.name}?
          </ModalHeader>
          <ModalFooter gap={3}>
            <Button variant="outline" onClick={onCancel} isDisabled={pending}>
              {"Ні"}
            </Button>
            <Button colorScheme="red" onClick={onConfirm} isLoading={pending}>
              {"Так"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
  );
}
